import React, { useEffect, useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useGetEngagementCampaignsQuery } from "../../../Data/services/engagementApi";
import { ErrorToast } from "../../../Hooks/useToast";
import { STATUS_COLORS, campaignLabel } from "../constants/labels";
import styles from "../styles/Dashboard.module.scss";
import { extractData, normalizeCampaigns } from "../utils";
import ChartSkeleton from "./ChartSkeleton";

export default function CampaignPerformanceSection({ onCampaignSelect }) {
  const {
    data: campaignsRes,
    isLoading,
    isError,
    error,
  } = useGetEngagementCampaignsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (isError) {
      ErrorToast(
        error?.data?.message || "Failed to load engagement campaigns"
      );
    }
  }, [isError, error]);

  const campaigns = normalizeCampaigns(extractData(campaignsRes));

  const campaignChartData = useMemo(
    () =>
      campaigns.map((c) => {
        const counts = c.send_counts || {};
        return {
          slug: c.slug,
          name: campaignLabel(c.slug, c.name),
          short: (campaignLabel(c.slug, c.name) || "").slice(0, 14),
          sent: Number(counts.sent) || 0,
          skipped: Number(counts.skipped) || 0,
          failed: Number(counts.failed) || 0,
        };
      }),
    [campaigns]
  );

  const campaignTotals = useMemo(
    () =>
      campaigns.reduce(
        (acc, c) => {
          const counts = c.send_counts || {};
          acc.sent += Number(counts.sent) || 0;
          acc.skipped += Number(counts.skipped) || 0;
          acc.failed += Number(counts.failed) || 0;
          return acc;
        },
        { sent: 0, skipped: 0, failed: 0 }
      ),
    [campaigns]
  );

  return (
    <div className={`${styles.panel} mt-4`}>
      <div className={styles.panelHeaderRow}>
        <div>
          <h5 className={styles.panelTitle}>Campaign performance</h5>
          <p className={styles.panelHint}>
            Sent / skipped / failed across engagement campaigns
          </p>
        </div>
        <div className={styles.miniKpis}>
          <span className={`${styles.miniKpi} ${styles.sent}`}>
            Sent {campaignTotals.sent}
          </span>
          <span className={`${styles.miniKpi} ${styles.skipped}`}>
            Skipped {campaignTotals.skipped}
          </span>
          <span className={`${styles.miniKpi} ${styles.failed}`}>
            Failed {campaignTotals.failed}
          </span>
        </div>
      </div>

      {isLoading ? (
        <ChartSkeleton height={320} />
      ) : campaigns.length === 0 ? (
        <div className={styles.emptyState}>No campaigns found.</div>
      ) : (
        <>
          <div className={styles.chartBox}>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart
                data={campaignChartData}
                margin={{ top: 12, right: 12, left: 0, bottom: 48 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="short"
                  interval={0}
                  angle={-25}
                  textAnchor="end"
                  height={60}
                  tick={{ fontSize: 11 }}
                />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar
                  dataKey="sent"
                  stackId="a"
                  fill={STATUS_COLORS.sent}
                  name="Sent"
                />
                <Bar
                  dataKey="skipped"
                  stackId="a"
                  fill={STATUS_COLORS.skipped}
                  name="Skipped"
                />
                <Bar
                  dataKey="failed"
                  stackId="a"
                  fill={STATUS_COLORS.failed}
                  name="Failed"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className={styles.tableWrap}>
            <table className={styles.dataTable}>
                <thead>
                  <tr>
                    <th>Campaign</th>
                    <th>Active</th>
                    <th>Sent</th>
                    <th>Skipped</th>
                    <th>Failed</th>
                    <th>Cooldown</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map((campaign) => {
                    const counts = campaign.send_counts || {};
                    const inactive = Number(campaign.is_active) === 0;
                    return (
                      <tr
                        key={campaign.slug}
                        className={inactive ? styles.mutedRow : undefined}
                      >
                        <td>
                          <div className={styles.campaignName}>
                            {campaignLabel(campaign.slug, campaign.name)}
                          </div>
                        </td>
                        <td>
                          <span
                            className={`${styles.badge} ${
                              inactive ? styles.badgeSkipped : styles.badgeSent
                            }`}
                          >
                            {inactive ? "Inactive" : "Active"}
                          </span>
                        </td>
                      <td>{Number(counts.sent) || 0}</td>
                      <td>{Number(counts.skipped) || 0}</td>
                      <td>{Number(counts.failed) || 0}</td>
                      <td>
                        {campaign.cooldown_days != null
                          ? `${campaign.cooldown_days}d`
                          : "—"}
                      </td>
                      <td>
                        <button
                          type="button"
                          className={styles.linkish}
                          onClick={() => onCampaignSelect?.(campaign)}
                        >
                          View users
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
